import { ChangedCells } from "@components/spreadsheet/SpreadsheetGrid";
import { ColumnPinInfo, FlatRow } from "@components/spreadsheet/types";
import { animated, useSpring } from "@react-spring/web";
import classNames from "classnames";
import React, { useEffect } from "react";
import { SPREADSHEET_ROW_HEIGHT } from "./constants";
import styles from "./SpreadsheetGrid.module.scss";
import { SpreadsheetGridCell } from "./SpreadsheetGridCell";
import {
    CellValue,
    ColumnDef,
    FillMode,
    SelectionRange,
    SpreadsheetAppearance,
    SpreadsheetCell,
    SpreadsheetData,
} from "./types";
import { isSpreadsheetActionsColumn } from "./spreadsheetActionsColumn";
import { getSpreadsheetRowLookAttributes } from "./spreadsheetRowLookAttributes";
import type { SpreadsheetRangeAction } from "./rangeAction";

export interface SpreadsheetGridCommonProps {
    selectedCell: SpreadsheetCell | null;
    rowIdx: number;
    selectionRange: SelectionRange;
    editingCell: SpreadsheetCell | null;
    row: Record<string, CellValue>;
    editedCellValue: CellValue;
    exitEditMode: (params?: {
        value: CellValue;
        rowIdx: number;
        colIdx: number;
    }) => void;
    fillMode: FillMode;
    isFirstRow: boolean;
    isLastRow: boolean;
    setEditedCellValue: (value?: CellValue) => void;
    handleCellMouseDown: (
        e: React.MouseEvent,
        rowIdx: number,
        colIdx: number,
    ) => void;
    handleCellMouseEnter: (rowIdx: number, colIdx: number) => void;
    handleFillHandleMouseDown: (
        e: React.MouseEvent,
        rowIdx: number,
        colIdx: number,
    ) => void;
    enterEditMode: (
        rowIdx: number,
        colIdx: number,
        e: React.MouseEvent | React.KeyboardEvent,
    ) => void;
    registerCellRef: (
        rowIdx: number,
        colIdx: number,
        element: HTMLDivElement | null,
    ) => void;
    handleAddRow: (rowIdx: number) => void;
    handleRemoveRow: (rowIdx: number) => void;
    enterEditModeKey: string | null;
    disableDragFill?: boolean;
    showDropdownIndicator?: boolean;
    isEmptyRow?: boolean;
    isCellEditable?: (rowIdx: number, colIdx: number) => boolean;
}

interface SpreadsheetGridRowProps extends SpreadsheetGridCommonProps {
    columns: ColumnDef[];
    columnPinInfo: Map<number, ColumnPinInfo>;
    className?: string;
    removedRowIndex: number | null;
    setRemovedRowIndex: (idx: number | null) => void;
    addedRowIndex: number | null;
    setAddedRowIndex: (idx: number | null) => void;
    handleChange: (
        newData: SpreadsheetData,
        changedCells: ChangedCells,
    ) => void;
    data: SpreadsheetData;
    expandable?: boolean;
    nestingIndentPx?: number;
    expandedIds?: Set<string>;
    onToggleExpand?: (id: string) => void;
    onRowClick?: (rowIdx: number) => void;
    /** Set only on the row holding the range action button, in column `rangeActionCol`. */
    rangeAction?: SpreadsheetRangeAction;
    rangeActionCol: number | null;
    appearance: SpreadsheetAppearance;
    /** Renders the empty filler cells at both ends (`SpreadsheetGrid` `centerColumns`). */
    centerColumns: boolean;
}

export const SpreadsheetGridRow: React.FC<SpreadsheetGridRowProps> = (
    props,
) => {
    const {
        className,
        columns,
        columnPinInfo,
        removedRowIndex,
        addedRowIndex,
        setRemovedRowIndex,
        setAddedRowIndex,
        handleChange,
        rowIdx,
        data,
        selectedCell,
        editingCell,
        expandable,
        nestingIndentPx,
        expandedIds,
        onToggleExpand,
        onRowClick,
        rangeAction,
        rangeActionCol,
        appearance,
        centerColumns,
        ...rest
    } = props;

    const flatRow = expandable ? (data[rowIdx] as FlatRow) : null;
    const depth = flatRow?._depth ?? 0;
    const hasChildren = flatRow?._hasChildren ?? false;
    const rowId = flatRow?._id ?? "";
    const isExpanded = rowId ? (expandedIds?.has(rowId) ?? false) : false;
    const lookAttributes = getSpreadsheetRowLookAttributes({
        appearance,
        data,
        rowIdx,
    });

    const filler = centerColumns && (
        <div
            className={classNames(styles.rowFiller, {
                [styles.lastRowCell]: rest.isLastRow,
            })}
            aria-hidden
        />
    );

    const [animation, api] = useSpring(() => ({
        height: SPREADSHEET_ROW_HEIGHT,
    }));

    useEffect(() => {
        if (removedRowIndex === rowIdx) {
            api.start({
                height: 0,
                onRest: () => {
                    const newData = [...data];
                    newData.splice(removedRowIndex, 1);
                    // Put empty array of changed cells here because removed cells could not be accessed
                    handleChange(newData, []);
                    setRemovedRowIndex(null);
                },
            });
        } else if (addedRowIndex === rowIdx) {
            api.set({ height: 0 });
            api.start({
                height: SPREADSHEET_ROW_HEIGHT,
                onRest: () => {
                    setAddedRowIndex(null);
                },
            });
        } else if (animation.height.get() === 0) {
            api.set({ height: SPREADSHEET_ROW_HEIGHT });
        }
    }, [
        api,
        animation,
        setRemovedRowIndex,
        setAddedRowIndex,
        removedRowIndex,
        addedRowIndex,
        rowIdx,
        data,
        handleChange,
    ]);

    return (
        <animated.div
            style={animation}
            className={classNames(styles.row, className, {
                [styles.clickableRow]: !!onRowClick,
            })}
            onClick={onRowClick ? () => onRowClick(rowIdx) : undefined}
            {...lookAttributes}
        >
            {filler}
            {columns.map((col, colIdx) => {
                const isSelected =
                    selectedCell?.row === rowIdx &&
                    selectedCell?.col === colIdx;
                const isEditing =
                    editingCell?.row === rowIdx && editingCell?.col === colIdx;
                const pinInfo = columnPinInfo.get(colIdx);
                if (isSpreadsheetActionsColumn(col)) {
                    return (
                        <div
                            key={col.field}
                            className={classNames(styles.actionsCell, {
                                [styles.lastRowCell]: rest.isLastRow,
                                [styles.pinnedRightCell]:
                                    pinInfo?.isPinnedRight,
                                [styles.firstPinnedRight]:
                                    pinInfo?.isFirstPinnedRight,
                                [styles.lastInRow]:
                                    colIdx === columns.length - 1,
                            })}
                            style={pinInfo?.pinnedStyle}
                            onMouseDown={(event) => {
                                event.stopPropagation();
                            }}
                        >
                            <div className={styles.cellContent}>
                                {col.actionsCellFn?.({
                                    row: rest.row,
                                    rowIdx,
                                    removeRow: rest.handleRemoveRow,
                                })}
                            </div>
                        </div>
                    );
                }
                return (
                    <SpreadsheetGridCell
                        key={col.field}
                        colIdx={colIdx}
                        rowIdx={rowIdx}
                        column={col}
                        isFirstCol={colIdx === 0}
                        isLastCol={colIdx === columns.length - 1}
                        isSelected={isSelected}
                        isEditing={isEditing}
                        pinInfo={pinInfo}
                        nestingDepth={colIdx === 0 && expandable ? depth : 0}
                        nestingIndentPx={nestingIndentPx}
                        hasChildren={colIdx === 0 ? hasChildren : false}
                        isExpanded={colIdx === 0 ? isExpanded : false}
                        onToggleExpand={
                            colIdx === 0 && hasChildren
                                ? () => onToggleExpand?.(rowId)
                                : undefined
                        }
                        hasTreeLead={
                            colIdx === 0 &&
                            expandable &&
                            appearance !== "spreadsheet"
                        }
                        rangeAction={
                            colIdx === rangeActionCol ? rangeAction : undefined
                        }
                        {...rest}
                    />
                );
            })}
            {filler}
        </animated.div>
    );
};
